import React, { useContext } from "react";

import { EditStageModalFormData } from "./EditStageModal";
import { GuideOptionsData } from "./GuideOptions";
import { RecipientManagerFormData } from "./RecipientManager";
import { SchedulerSettingsManagerFormData } from "./SchedulerSettingsManager";
import AtsStage from "./types/AtsStage";
import ContentTemplate from "./types/ContentTemplate";
import EmailTemplate from "./types/EmailTemplate";
import { EventTemplate } from "./types/EventTemplate";
import PostTemplate from "./types/PostTemplate";
import Stage from "./types/Stage";

export const enum ProviderType {
  GUIDE = "Guide",
  GUIDE_TEMPLATE = "GuideTemplate",
  JOB_ROLE_GUIDE_TEMPLATE = "JobRoleGuideTemplate",
}

export interface GuideEditorContextValue {
  id: string;
  type: ProviderType;
  inUse: boolean;
  atsStages?: AtsStage[];
  nylasAccessTokenExists?: boolean;
  interviews?: EventTemplate[];
  getEditStageInitialValues: (stage?: Stage) => EditStageModalFormData;
  setContentTemplate: (input: {
    stage: Stage;
    contentTemplate: ContentTemplate;
  }) => Promise<unknown>;
  setPostTemplate: (input: {
    stage: Stage;
    postTemplate: PostTemplate;
  }) => Promise<unknown>;
  setEmailTemplate: (input: {
    stage: Stage;
    emailTemplate: EmailTemplate;
  }) => Promise<unknown>;
  removeContentTemplate: (stage: Stage) => Promise<unknown>;
  removePostTemplate: (stage: Stage) => Promise<unknown>;
  removeEmailTemplate: (stage: Stage) => Promise<unknown>;
  saveOptions: (data: Partial<GuideOptionsData>) => Promise<unknown>;
  createStage: (data: EditStageModalFormData) => Promise<unknown>;
  isCreateStageLoading: boolean;
  updateStage: (
    data: Omit<EditStageModalFormData, "name" | "description"> & {
      name?: string | null;
      description?: string | null;
    },
    options?: { useOptimisticResponse: boolean }
  ) => Promise<unknown>;
  isZeusJob: boolean;
  isUpdateStageLoading: boolean;
  reorder: (stages: Stage[]) => Promise<unknown>;
  removeStage: (stage: Stage) => Promise<unknown>;
  hideStage?: (stageId: string) => Promise<unknown>;
  showStage?: (stageId: string) => Promise<unknown>;
  updateRecipients?: (data: RecipientManagerFormData) => Promise<unknown>;
  updateSchedulerSettings?: (
    data: SchedulerSettingsManagerFormData
  ) => Promise<unknown>;
  selectedStageId: string | null;
  setSelectedStageId: (id: string | null) => void;
  stages: Stage[];
  selectedStage: Stage | null;
  hideMissingStagesSupportLink?: boolean;
  disableHiringPlanModification?: boolean;
  text: {
    disableHiringPlanModificationText?: string;
    removeStageConfirmationText: React.ReactNode;
    removeContentTemplateConfirmationText: React.ReactNode;
    updateContentTemplateConfirmationText: React.ReactNode;
    removeEmailTemplateConfirmationText: React.ReactNode;
    updateEmailTemplateConfirmationText: React.ReactNode;
    reorderStagesConfirmationText: React.ReactNode;
    hideStageConfirmationText?: React.ReactNode;
    showStageConfirmationText?: React.ReactNode;
  };
  hideSchedulerSettings?: boolean;
  hideRecipients?: boolean;
}

const GuideEditorContext = React.createContext<
  GuideEditorContextValue | undefined
>(undefined);

export const useGuideEditorContext = () => {
  const context = useContext(GuideEditorContext);
  if (context === undefined) {
    throw new Error("EditorContext must be used within EditorContextProvider");
  }
  return context;
};

export default GuideEditorContext;
